/*
 * Project: bellcloud
 * File: packages/types/errors/I18nError.ts
 * Author: SWAN
 * Created: 2025-11-04
 * Description: 구조화된 i18n 에러 클래스
 */

/**
 * i18n 키를 포함한 구조화된 에러 클래스
 *
 * 사용 예:
 * ```typescript
 * throw new I18nError('config:error_bleConnectionFailed', {
 *   deviceName: 'BELLMINI_001'
 * });
 * ```
 *
 * 에러 핸들러에서:
 * ```typescript
 * if (error instanceof I18nError) {
 *   const message = t(error.i18nKey, error.params);
 *   showError(message);
 * }
 * ```
 */
export class I18nError extends Error {
  /**
   * i18n 번역 키 (예: 'config:error_bleConnectionFailed')
   */
  public readonly i18nKey: string;

  /**
   * i18n 번역 파라미터 (선택사항)
   */
  public readonly params?: Record<string, unknown>;

  /**
   * 원본 에러 (선택사항)
   */
  public readonly cause?: Error;

  constructor(i18nKey: string, params?: Record<string, unknown>, cause?: Error) {
    // Error 생성자에는 i18n 키를 그대로 전달 (fallback용)
    super(i18nKey);

    this.name = 'I18nError';
    this.i18nKey = i18nKey;
    this.params = params;
    this.cause = cause;

    // 스택 트레이스 유지
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, I18nError);
    }
  }

  /**
   * 에러를 JSON으로 직렬화
   */
  toJSON() {
    return {
      name: this.name,
      i18nKey: this.i18nKey,
      params: this.params,
      message: this.message,
      stack: this.stack,
    };
  }
}

/**
 * 에러가 I18nError인지 타입 가드
 */
function isI18nError(error: unknown): error is I18nError {
  return error instanceof I18nError;
}

/**
 * 에러에서 i18n 키 추출 (I18nError가 아니면 기본 키 반환)
 */
export function getI18nKey(error: unknown, defaultKey = 'common:error_unknownError'): string {
  if (isI18nError(error)) {
    return error.i18nKey;
  }

  // 레거시: 에러 메시지가 i18n 키 형식인 경우 (xxx:yyy)
  if (error instanceof Error && error.message.includes(':')) {
    return error.message;
  }

  return defaultKey;
}
